// frontend/components/CrmWhatsAppPanel.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCrmCommunications, sendCrmWhatsAppMessage } from "../features/crm/crmThunks.js";
import "../styles/crm/crmwhatsapp.css";

const buildFirstName = (fullName) => {
  const clean = (fullName || "").trim();
  if (!clean) return "";
  return clean.split(" ")[0] || "";
};

const applyPlaceholders = (template, { name }) => {
  const safeTemplate = template || "";
  return safeTemplate.replaceAll("{name}", name || "");
};

const getFallbackTemplateByStageName = (stageNameRaw, contactFirstName) => {
  const stageName = (stageNameRaw || "").toLowerCase();

  const greeting = contactFirstName ? `Hola ${contactFirstName},` : "Hola,";
  const signature = "\n\n— Equipo de la clínica";

  if (stageName.includes("lead nuevo") || (stageName.includes("lead") && stageName.includes("nuevo"))) {
    return `${greeting}\nGracias por tu interés 😊\n¿Me confirmas qué tratamiento te interesa y tu disponibilidad para una primera valoración?${signature}`;
  }

  if (stageName.includes("primer contacto")) {
    return `${greeting}\nSoy del equipo de la clínica 😊\n¿Te va bien que te haga unas preguntas rápidas para recomendarte la mejor opción?${signature}`;
  }

  if (stageName.includes("visita") || stageName.includes("clínica") || stageName.includes("clinica")) {
    return `${greeting}\nTe escribo para coordinar tu visita en clínica.\n¿Te encaja venir esta semana? Dime qué días/horas te van mejor y lo cerramos.${signature}`;
  }

  if (stageName.includes("presupuesto") || stageName.includes("enviado")) {
    return `${greeting}\nTe acabo de enviar el presupuesto.\nCuando lo revises, me dices cualquier duda y te ayudo a decidir el siguiente paso 😊${signature}`;
  }

  if (stageName.includes("cerrado ganado") || (stageName.includes("cerrado") && stageName.includes("ganado")) || stageName.includes("ganado")) {
    return `${greeting}\n¡Genial! Confirmamos el siguiente paso.\nTe envío ahora los detalles y la documentación necesaria para continuar.${signature}`;
  }

  if (stageName.includes("cerrado perdido") || (stageName.includes("cerrado") && stageName.includes("perdido")) || stageName.includes("perdido")) {
    return `${greeting}\nGracias por tu tiempo.\nSi en el futuro quieres retomarlo, aquí estamos. Si te apetece, ¿qué te frenó para poder mejorar?${signature}`;
  }

  return `${greeting}\nTe escribo por tu caso. ¿Cómo quieres que avancemos?${signature}`;
};

const CrmWhatsAppPanel = ({
  contactId,
  dealId,
  defaultPhone = "",
  pipelineId: pipelineIdProp,
  stageId: stageIdProp,
  onClose,
}) => {
  const dispatch = useDispatch();

  const crmState = useSelector((state) => state.crm || {});
  const communications = crmState.communications || [];
  const deals = crmState.deals || [];
  const pipelines = crmState.pipelines || [];
  const contacts = crmState.contacts || [];
  const error = crmState.error;

  const [toPhone, setToPhone] = useState(defaultPhone || "");
  const [content, setContent] = useState("");
  const [localError, setLocalError] = useState(null);
  const [isSending, setIsSending] = useState(false);

  const [selectedStageId, setSelectedStageId] = useState(null);

  useEffect(() => {
    if (!toPhone.trim() && (defaultPhone || "").trim()) {
      setToPhone(defaultPhone.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPhone]);

  useEffect(() => {
    if (!contactId && !dealId) return;

    dispatch(
      fetchCrmCommunications({
        contactId: contactId || undefined,
        dealId: dealId || undefined,
        channel: "whatsapp",
      })
    );
  }, [dispatch, contactId, dealId]);

  const whatsappMessages = useMemo(() => {
    return communications.filter((message) => message.channel === "whatsapp");
  }, [communications]);

  const currentDeal = useMemo(() => {
    if (!dealId) return null;
    return deals.find((deal) => String(deal.id) === String(dealId)) || null;
  }, [deals, dealId]);

  const resolvedPipelineId = pipelineIdProp ?? currentDeal?.pipeline ?? null;
  const resolvedStageId = stageIdProp ?? currentDeal?.stage ?? null;

  const currentContact = useMemo(() => {
    if (!contactId) return null;
    return contacts.find((contact) => String(contact.id) === String(contactId)) || null;
  }, [contacts, contactId]);

  const contactFullName = currentContact?.full_name || "Contacto";
  const contactFirstName = useMemo(() => buildFirstName(currentContact?.full_name), [currentContact]);

  const currentPipelineStages = useMemo(() => {
    if (!resolvedPipelineId) return [];
    const pipeline = pipelines.find((p) => String(p.id) === String(resolvedPipelineId));
    const stages = pipeline?.stages ? pipeline.stages : [];
    return stages.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [pipelines, resolvedPipelineId]);

  useEffect(() => {
    if (!selectedStageId && resolvedStageId) {
      setSelectedStageId(String(resolvedStageId));
    }
  }, [resolvedStageId, selectedStageId]);

  const selectedStage = useMemo(() => {
    if (!selectedStageId) return null;
    return currentPipelineStages.find((stage) => String(stage.id) === String(selectedStageId)) || null;
  }, [currentPipelineStages, selectedStageId]);

  const computedTemplateText = useMemo(() => {
    const stageName = selectedStage?.name || "";
    const stageTemplate = (selectedStage?.whatsapp_template || "").trim();

    const rawTemplate = stageTemplate || getFallbackTemplateByStageName(stageName, contactFirstName);
    return applyPlaceholders(rawTemplate, { name: contactFirstName });
  }, [selectedStage, contactFirstName]);

  useEffect(() => {
    if (!selectedStageId) return;
    setContent(computedTemplateText);
  }, [selectedStageId, computedTemplateText]);

  const handleSend = async (event) => {
    event.preventDefault();
    setLocalError(null);

    if (!toPhone.trim()) {
      setLocalError("Falta el teléfono (formato +34...).");
      return;
    }
    if (!content.trim()) {
      setLocalError("Escribe un mensaje.");
      return;
    }

    setIsSending(true);

    const result = await dispatch(
      sendCrmWhatsAppMessage({
        contactId,
        dealId,
        toPhone: toPhone.trim(),
        content: content.trim(),
      })
    );

    if (sendCrmWhatsAppMessage.fulfilled.match(result)) {
      // ✅ deja de “vaciar” y vuelve a plantilla, para trabajar rápido como CRM
      setContent(computedTemplateText);

      dispatch(
        fetchCrmCommunications({
          contactId: contactId || undefined,
          dealId: dealId || undefined,
          channel: "whatsapp",
        })
      );
    } else {
      const detail = (result.payload && result.payload.detail) || "No se pudo enviar.";
      setLocalError(detail);
    }

    setIsSending(false);
  };

  return (
    <div className="crm-wa-card">
      <div className="crm-wa-header">
        <div className="crm-wa-header-left">
          <h3 className="crm-wa-title">WhatsApp</h3>
          <p className="crm-wa-subtitle">Mensajes y seguimiento.</p>
        </div>

        <div className="crm-wa-header-right">
          <div className="crm-wa-contact">
            <div className="crm-wa-contact-name" title={contactFullName}>
              {contactFullName}
            </div>
            <div className="crm-wa-contact-meta" title={toPhone || ""}>
              {toPhone || "—"}
            </div>
          </div>
        </div>
      </div>

      <form className="crm-wa-form" onSubmit={handleSend}>
        <div className="crm-wa-form-grid">
          {currentPipelineStages.length > 0 ? (
            <div className="crm-wa-field">
              <label htmlFor="selectedStageId">Fase</label>
              <select
                id="selectedStageId"
                className="crm-wa-input"
                value={selectedStageId || ""}
                onChange={(event) => setSelectedStageId(event.target.value)}
              >
                <option value="" disabled>
                  Selecciona una fase…
                </option>
                {currentPipelineStages.map((stage) => (
                  <option key={stage.id} value={String(stage.id)}>
                    {stage.order}. {stage.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="crm-wa-field">
            <label htmlFor="toPhone">Teléfono</label>
            <input
              id="toPhone"
              className="crm-wa-input"
              value={toPhone}
              onChange={(event) => setToPhone(event.target.value)}
              placeholder="+34600111222"
            />
          </div>
        </div>

        <div className="crm-wa-field">
          <label htmlFor="content">Mensaje</label>
          <textarea
            id="content"
            className="crm-wa-textarea"
            value={content}
            onChange={(event) => setContent(event.target.value)}
            placeholder="Escribe el mensaje…"
            rows={5}
          />
        </div>

        {(localError || (error && error.detail)) ? (
          <div className="crm-wa-message crm-wa-message--error">{localError || error.detail}</div>
        ) : null}

        {/* ✅ ACCIONES: cerrar dentro del form */}
        <div className="crm-wa-actions">
          <button
            type="button"
            className="crm-wa-btn crm-wa-btn--ghost"
            onClick={() => {
              if (typeof onClose === "function") onClose();
            }}
          >
            Cerrar
          </button>

          <button className="crm-wa-btn crm-wa-btn--primary" type="submit" disabled={isSending}>
            {isSending ? "Enviando…" : "Enviar WhatsApp"}
          </button>
        </div>
      </form>

      <div className="crm-wa-divider" />

      <div className="crm-wa-thread" aria-label="Historial de WhatsApp">
        {whatsappMessages.length === 0 ? (
          <div className="crm-wa-empty">No hay mensajes todavía.</div>
        ) : (
          whatsappMessages.map((message) => (
            <div
              key={message.id}
              className={
                message.direction === "outbound"
                  ? "crm-wa-bubble crm-wa-bubble--out"
                  : "crm-wa-bubble crm-wa-bubble--in"
              }
            >
              <div className="crm-wa-bubble-text">{message.content || "-"}</div>
              <div className="crm-wa-bubble-meta">
                <span>{message.direction === "outbound" ? "Enviado" : "Recibido"}</span>
                <span className="crm-wa-bubble-sep">·</span>
                <span>{message.status || "-"}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CrmWhatsAppPanel;